import {
  addPileToCurrentOrder,
  setDefaultPiles,
  updateCurrentOrderPile,
} from "Redux/features/pile";
import { AppThunk } from "Redux/store";
import { isAxiosResponse } from "Services/index";
import {
  postOrderItem,
  updateOrderItem,
  getItemsCount,
} from "Services/order";
import { getPiles } from "Services/pile";
import { getMedia } from "Services/media";
import { PILE_SUBCLASS_TYPE } from "Constants/pile-constants";

/*
 * Prevents multiple pile creations from calculating
 * the same row_number at the same time.
 */
let createPileQueue: Promise<any> = Promise.resolve();

const getPreviewImageId = (previewImage: any): number | string | null => {
  if (!previewImage) {
    return null;
  }

  if (
    typeof previewImage === "number" ||
    typeof previewImage === "string"
  ) {
    return previewImage;
  }

  return previewImage.id ?? null;
};

const loadPilePreviewImage = async (pileItem: any) => {
  const previewID = getPreviewImageId(
    pileItem?.tool?.preview_image
  );

  if (!previewID) {
    throw new Error("לא התקבל מזהה לתמונת הכלונס.");
  }

  const mediaResponse = await getMedia(previewID);

  if (!isAxiosResponse(mediaResponse)) {
    throw new Error("טעינת תמונת הכלונס נכשלה.");
  }

  return {
    ...pileItem,
    tool: {
      ...pileItem.tool,
      preview_image: {
        ...mediaResponse.data,
      },
    },
  };
};

// NOTE:
// rowNum === 0 when creating a new item.
// rowNum > 0 when duplicating an existing item.
export const createPile =
  (
    pile: any,
    orderItem: any,
    next = (_val: boolean) => {},
    rowNum = 0,
    orderId = 0
  ): AppThunk =>
  async (dispatch, getState) => {
    const createTask = async () => {
      try {
        const user = getState().user.userData;

        /*
         * Logged-in flow
         */
        if (user) {
          let rowNumber = rowNum;

          /*
           * Only calculate the next row when this is a regular creation.
           * During duplication, the requested row number is supplied
           * by the duplication flow.
           */
          if (rowNumber <= 0) {
            const countResponse = await getItemsCount(
              PILE_SUBCLASS_TYPE,
              orderId
            );

            if (!isAxiosResponse(countResponse)) {
              throw new Error("קבלת מספר השורה הבא נכשלה.");
            }

            rowNumber = Number(countResponse.data.row_number) + 1;
          }

          const payload = {
            ...orderItem,
            row_number: rowNumber,
            ...(orderId > 0 ? { order: orderId } : {}),
            tool: pile,
            user: user.id,
          };

          const response = await postOrderItem(payload);

          if (!isAxiosResponse(response)) {
            throw new Error("שמירת הכלונס נכשלה.");
          }

          const orderIt = await loadPilePreviewImage(response.data);

          /*
           * Always add a successfully-created pile to Redux.
           * The old condition could leave the item on the server
           * without showing it until a page refresh.
           */
          await dispatch(addPileToCurrentOrder(orderIt));

          next(true);

          return {
            success: true,
            data: orderIt,
          };
        }

        /*
         * Offline / guest flow
         */
        const pileData = {
          ...orderItem,
          tool: pile,
        };

        const orderIt = await loadPilePreviewImage(pileData);

        await dispatch(addPileToCurrentOrder(orderIt));

        next(true);

        return {
          success: true,
          data: orderIt,
        };
      } catch (error) {
        console.error("Failed to create pile:", error);

        next(false);

        return {
          success: false,
          message:
            error instanceof Error
              ? error.message
              : "שמירת הכלונס נכשלה.",
        };
      }
    };

    /*
     * Run pile creations sequentially.
     *
     * The second callback also uses createTask so the queue continues
     * even if a previous creation failed.
     */
    const queuedTask = createPileQueue.then(
      createTask,
      createTask
    );

    /*
     * Keep the queue alive without preserving a rejected state.
     */
    createPileQueue = queuedTask.then(
      () => undefined,
      () => undefined
    );

    return queuedTask;
  };

export const updatePile =
  (
    orderItemID: number,
    pile: any,
    orderItem: any,
    next = (_val: boolean) => {}
  ): AppThunk =>
  async (dispatch, getState) => {
    try {
      const user = getState().user.userData;

      if (!user) {
        throw new Error("לא נמצא משתמש מחובר.");
      }

      const response = await updateOrderItem(orderItemID, {
        ...orderItem,
        tool: pile,
      });

      if (!isAxiosResponse(response)) {
        throw new Error("עדכון הכלונס נכשל.");
      }

      const orderIt = await loadPilePreviewImage(response.data);

      await dispatch(updateCurrentOrderPile(orderIt));

      next(true);

      return {
        success: true,
        data: orderIt,
      };
    } catch (error) {
      console.error("Failed to update pile:", error);

      next(false);

      return {
        success: false,
        message:
          error instanceof Error
            ? error.message
            : "עדכון הכלונס נכשל.",
      };
    }
  };

export const fetchDefaultsPiles =
  (): AppThunk =>
  async (dispatch) => {
    try {
      const shapes = await getPiles(2);

      if (Array.isArray(shapes) && shapes.length > 0) {
        await dispatch(setDefaultPiles(shapes));
      }

      return {
        success: true,
        data: shapes,
      };
    } catch (error) {
      console.error(
        "Failed to fetch default piles:",
        error
      );

      return {
        success: false,
        message:
          error instanceof Error
            ? error.message
            : "טעינת ברירות המחדל של הכלונסאות נכשלה.",
      };
    }
  };